import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useField, useFormikContext } from "formik";
import { useMediaQuery } from "react-responsive";
import { Input } from "reactstrap";
import { Button } from "../Button";
import { useTranslation } from "react-i18next";

import EmojiPicker from "../EmojiPicker";
import { XL } from "@const/breakpoints";
import {
  MAX_COMMENT_LENGTH,
  MAX_COMMENT_TEXTAREA_HEIGHT,
} from "@validation/lengthConstants";
import { getMaxLength } from "@helpers/validation";

const CommentForm = ({
  styles,
  handleEnter,
  className,
  withAutofocus,
  postFooterMobile,
}) => {
  const { t } = useTranslation("buttons");
  const { t: t1 } = useTranslation("placeholders");
  const { handleSubmit, isValid } = useFormikContext();
  const inputRef = useRef();

  const [textAreaHeight, setTextAreaHeight] = useState("auto");
  const [parentHeight, setParentHeight] = useState("auto");

  const isDesktop = useMediaQuery({
    query: `(min-width: ${XL})`,
  });

  const [{ onChange, value, ...field }, _meta, { setValue }] = useField({
    name: "message",
    type: "text",
  });

  useEffect(() => {
    if (value !== "") {
      const scrollHeight =
        inputRef?.current?.scrollHeight > MAX_COMMENT_TEXTAREA_HEIGHT &&
        isDesktop
          ? MAX_COMMENT_TEXTAREA_HEIGHT
          : inputRef?.current?.scrollHeight;
      const scrollHeightPx = `${scrollHeight}px`;
      setParentHeight(scrollHeightPx);
      setTextAreaHeight(scrollHeightPx);
    } else {
      setParentHeight("auto");
      setTextAreaHeight("auto");
    }
  }, [value]);

  const maxCalculatedLength = getMaxLength(value, MAX_COMMENT_LENGTH);
  // support maxLength for mobile
  useEffect(() => {
    if (!value) return;
    if (value.length <= maxCalculatedLength) return;
    setValue(value.slice(0, maxCalculatedLength));
  }, [value, setValue, maxCalculatedLength]);

  return (
    <form
      onKeyDown={handleEnter}
      onSubmit={handleSubmit}
      className={classNames(
        "position-sticky start-0 bottom-0 flex-shrink-0 flex-column w-100 h-fit-content",
        className,
      )}
    >
      <div className={classNames("h-100", styles.scrollSend)}>
        {postFooterMobile}
        <section
          className={classNames(
            "form-control-wrapper pb-0",
            styles.commentInput,
          )}
        >
          <div
            className={classNames("d-flex", className, styles.commentWrapper)}
            style={{
              minHeight: parentHeight,
            }}
          >
            <Input
              {...field}
              autoFocus={isDesktop && withAutofocus}
              innerRef={inputRef}
              name="message"
              maxLength={maxCalculatedLength}
              className={classNames(
                "text-emoji-regular py-3 form-control auto",
                styles.textarea,
                styles.formikChatInput,
              )}
              rows={1}
              value={value}
              type="textarea"
              onChange={onChange}
              style={{
                height: textAreaHeight,
                overflowY: isDesktop ? "auto" : "hidden",
              }}
              placeholder={t1("ADD_COMMENT")}
            />
          </div>
        </section>
      </div>
      <div className={classNames("position-absolute", styles.buttons)}>
        <EmojiPicker
          emojiClassName={styles.formikChatEmojiButton}
          value={value}
          setValue={setValue}
          inputRef={inputRef}
          placement="top-end"
        />
        <Button
          type="submit"
          color="initial"
          disabled={!isValid}
          className={classNames(
            "btn-resetStyle text-body-1 text-medium",
            styles.submit,
          )}
        >
          <span className={classNames("text-body-2")}>{t("SEND")}</span>
        </Button>
      </div>
    </form>
  );
};

export default CommentForm;
